import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircleIcon, XCircleIcon, CloudArrowUpIcon, VideoCameraIcon } from '@heroicons/react/24/outline';

const OnboardingSplash = ({ onComplete }) => {
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const videoRef = useRef(null);
  const lastSentBucketRef = useRef(-1);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Secret skip feature: click video title 5 times rapidly
  const [titleClicks, setTitleClicks] = useState(0);
  const [clickTimer, setClickTimer] = useState(null);

  // Logout handler - defined early so it's available in all return statements
  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  // Secret skip handler: Click video title 5 times in 3 seconds
  const handleTitleClick = async () => {
    const newClicks = titleClicks + 1;
    setTitleClicks(newClicks);
    
    // Clear existing timer
    if (clickTimer) {
      clearTimeout(clickTimer);
    }
    
    // Check if reached 5 clicks
    if (newClicks >= 5) {
      // Secret activated! Mark video as watched
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('watch_time', '999');
        formData.append('total_time', '1000');
        
        await fetch(`${backendUrl}/api/onboarding/update-video-progress`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        setVideoWatched(true);
        setOnboardingData({
          ...onboardingData,
          video_watched: true
        });
        console.log('🎉 Secret skip activated!');
      } catch (error) {
        console.error('Failed to skip video:', error);
      }
      
      setTitleClicks(0);
      return;
    }
    
    // Reset counter after 3 seconds
    const timer = setTimeout(() => {
      setTitleClicks(0);
    }, 3000);
    
    setClickTimer(timer);
  };

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  useEffect(() => {
    if (onboardingData) {
      checkIfComplete();
    }
  }, [onboardingData]);

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/onboarding/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setOnboardingData(data.onboarding_data);
      setVideoWatched(data.onboarding_data?.video_watched || false);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
      setLoading(false);
    }
  };

  const checkIfComplete = () => {
    // Only require video to be watched
    const required = [
      onboardingData?.video_watched
    ];
    setCanSubmit(required.every(item => item));
  };

  const getMissingItems = () => {
    const missing = [];
    
    // Only check video
    if (!onboardingData?.video_watched) {
      const watchedSeconds = onboardingData?.video_watch_time || 0;
      const totalSeconds = onboardingData?.video_total_time || 0;
      const remainingSeconds = Math.max(0, totalSeconds * 0.95 - watchedSeconds);
      const remainingMinutes = Math.ceil(remainingSeconds / 60);
      
      if (totalSeconds > 0) {
        missing.push(`Video: ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''} remaining`);
      } else {
        missing.push('Safety Training Video: Not started');
      }
    }
    
    return missing;
  };

  const handleVideoProgress = async () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      if (!duration || isNaN(duration)) return;

      // Throttle: send progress at most once every 5 seconds of playback
      const bucket = Math.floor(currentTime / 5);
      if (bucket === lastSentBucketRef.current) return;
      lastSentBucketRef.current = bucket;

      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('watch_time', currentTime);
        formData.append('total_time', duration);

        const res = await fetch(`${backendUrl}/api/onboarding/update-video-progress`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const data = await res.json();
        if (data?.video_watched) {
          setVideoWatched(true);
        }
      } catch (error) {
        console.error('Failed to update video progress:', error);
      }
    }
  };

  const handleVideoEnd = async () => {
    // Post a final progress update so the backend marks the video complete
    try {
      const token = localStorage.getItem('token');
      const duration = videoRef.current?.duration || 100;
      const formData = new FormData();
      formData.append('watch_time', duration);
      formData.append('total_time', duration);
      await fetch(`${backendUrl}/api/onboarding/update-video-progress`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
    } catch (error) {
      console.error('Failed to mark video complete:', error);
    }
    setVideoWatched(true);
    await fetchOnboardingStatus();
  };

  const handleManualVideoComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('watch_time', 100);
      formData.append('total_time', 100);

      const response = await fetch(`${backendUrl}/api/onboarding/update-video-progress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setVideoWatched(true);
        await fetchOnboardingStatus();
      }
    } catch (error) {
      console.error('Failed to mark video as watched:', error);
    }
  };

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);

      const response = await fetch(`${backendUrl}/api/onboarding/upload-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        await fetchOnboardingStatus();
      } else {
        alert('Failed to upload document');
      }
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const [ticketFiles, setTicketFiles] = useState([]);
  const [dupNote, setDupNote] = useState('');
  const loadTicketFiles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${backendUrl}/api/onboarding/my-safety-tickets`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setTicketFiles(await r.json());
    } catch (e) { /* ignore */ }
  }, [backendUrl]);
  useEffect(() => { loadTicketFiles(); }, [loadTicketFiles]);
  useEffect(() => {
    if (!ticketFiles.some((t) => t.drive_status === 'pending' || t.drive_status === 'filing')) return undefined;
    const id = setInterval(loadTicketFiles, 5000);
    return () => clearInterval(id);
  }, [ticketFiles, loadTicketFiles]);

  const handleMultiUpload = async (documentType, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', documentType);
        const response = await fetch(`${backendUrl}/api/onboarding/upload-document`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (!response.ok) { alert(`Failed to upload ${file.name}`); continue; }
        if (documentType === 'safety_ticket') {
          const d = await response.json().catch(() => ({}));
          if (d.duplicate) setDupNote(`${file.name} was already uploaded — skipped`);
        }
      }
      await fetchOnboardingStatus();
      if (documentType === 'safety_ticket') loadTicketFiles();
    } catch (error) {
      console.error('Failed to upload documents:', error);
      alert('Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const handleVoidChequeUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${backendUrl}/api/onboarding/upload-void-cheque`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        await fetchOnboardingStatus();
      } else {
        alert('Failed to upload void cheque');
      }
    } catch (error) {
      console.error('Failed to upload void cheque:', error);
      alert('Failed to upload void cheque');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    // Just redirect to dashboard after video is watched
    window.location.href = '/dashboard';
  };

  const DocumentUpload = ({ label, documentType, uploaded, testid }) => (
    <div className="border border-gray-300 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium">{label}</span>
        {uploaded ? (
          <CheckCircleIcon className="h-6 w-6 text-green-500" />
        ) : (
          <XCircleIcon className="h-6 w-6 text-slate-700" />
        )}
      </div>
      <input
        type="file"
        accept="image/*,.pdf"
        onChange={(e) => handleFileUpload(documentType, e.target.files[0])}
        className="text-sm"
        disabled={uploading}
        data-testid={testid}
      />
      {uploaded && <p className="text-xs text-green-600 mt-1">✓ Uploaded</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (onboardingData?.status === 'submitted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center relative">
          {/* Logout button */}
          <button
            onClick={handleLogout}
            className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
          
          <div className="mb-4">
            <CloudArrowUpIcon className="h-16 w-16 text-blue-600 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Onboarding Submitted!</h2>
          <p className="text-gray-600 mb-4">
            Your onboarding documents have been submitted and are awaiting HR review.
            You'll receive an email notification once approved.
          </p>
          <p className="text-sm text-gray-500">
            Submitted on: {new Date(onboardingData.submitted_at).toLocaleString(undefined, { timeZone: 'America/Toronto' })}
          </p>
        </div>
      </div>
    );
  }

  if (onboardingData?.status === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center relative">
          {/* Logout button */}
          <button
            onClick={handleLogout}
            className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
          
          <div className="mb-4">
            <XCircleIcon className="h-16 w-16 text-red-600 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Onboarding Rejected</h2>
          <p className="text-gray-600 mb-4">
            Your onboarding submission was rejected. Please review the feedback and resubmit.
          </p>
          {onboardingData.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <p className="text-sm text-red-800">{onboardingData.rejection_reason}</p>
            </div>
          )}
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Update and Resubmit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Logout button in top right */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">Welcome to 365 Industrial Solutions!</h1>
              <p className="text-gray-600">Please complete all items below to finish your onboarding.</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>

          {/* Video Section */}
          <div className="mb-8">
            <h2 
              className="text-xl font-semibold mb-4 flex items-center cursor-pointer select-none"
              onClick={handleTitleClick}
              title="Safety Training Video"
            >
              <VideoCameraIcon className="h-6 w-6 mr-2" />
              1. Watch Safety Training Video
              {videoWatched && <CheckCircleIcon className="h-6 w-6 text-green-500 ml-2" />}
            </h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-sm">
              <p className="text-blue-800">
                <strong>Safety Training Video:</strong> Watch the full video below. 
                Use the fullscreen button on the player for full-screen viewing — your progress is saved automatically.
              </p>
            </div>
            <div className="bg-white/60 backdrop-blur-xl border border-slate-200 p-4 rounded-lg">
              {/* Video Player Container */}
              <div className="relative bg-white rounded overflow-hidden" style={{aspectRatio: '16/9'}}>
                <video
                  ref={videoRef}
                  src={`${backendUrl}/api/onboarding/safety-training-video`}
                  controls
                  controlsList="nodownload"
                  playsInline
                  preload="metadata"
                  onTimeUpdate={handleVideoProgress}
                  onEnded={handleVideoEnd}
                  className="absolute inset-0 w-full h-full"
                  style={{ backgroundColor: '#000' }}
                  data-testid="safety-training-video"
                  title="Safety Training Video"
                />
              </div>
              
              {/* Video Status */}
              <div className="mt-3 space-y-2">
                <p className="text-center text-sm text-slate-600" data-testid="video-status-text">
                  {videoWatched ? (
                    <span className="text-green-600 font-medium">✓ Video completed</span>
                  ) : (
                    <span>Watch the full video to continue. Tip: use the fullscreen button on the player.</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Document uploads — optional, take a photo of each */}
          <div className="mb-8" data-testid="splash-documents-section">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <CloudArrowUpIcon className="h-6 w-6 mr-2" />
              2. Upload Your Documents <span className="ml-2 text-sm font-normal text-gray-500">(optional — you can also add these later)</span>
            </h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
              Snap a clear photo of each document with your phone. These are filed straight into your employee records folder.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DocumentUpload
                label="Health Card (front)"
                documentType="health_card_front"
                uploaded={!!onboardingData?.health_card_front}
                testid="upload-health-card-front"
              />
              <DocumentUpload
                label="Health Card (back)"
                documentType="health_card_back"
                uploaded={!!onboardingData?.health_card_back}
                testid="upload-health-card-back"
              />
              <DocumentUpload
                label="Driver's License (front)"
                documentType="drivers_license_front"
                uploaded={!!onboardingData?.drivers_license_front}
                testid="upload-drivers-license-front"
              />
              <DocumentUpload
                label="Driver's License (back)"
                documentType="drivers_license_back"
                uploaded={!!onboardingData?.drivers_license_back}
                testid="upload-drivers-license-back"
              />
            </div>

            {/* Certificates / safety tickets — allow several */}
            <div className="border border-gray-300 rounded-lg p-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Certificates / Safety Tickets</span>
                {(onboardingData?.safety_tickets?.length || 0) > 0 ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-slate-700" />
                )}
              </div>
              <p className="text-xs text-gray-500 mb-2">You can add several — select or photograph one or more at a time.</p>
              <input
                type="file"
                accept="image/*,.pdf"
                multiple
                onChange={(e) => { const fl = e.target.files; handleMultiUpload('safety_ticket', fl); e.target.value = ''; }}
                className="text-sm"
                disabled={uploading}
                data-testid="upload-certificates"
              />
              {(onboardingData?.safety_tickets?.length || 0) > 0 && (
                <p className="text-xs text-green-600 mt-1" data-testid="certificates-count">
                  ✓ {onboardingData.safety_tickets.length} uploaded
                </p>
              )}
              {dupNote && <p className="text-xs text-amber-700 mt-1" data-testid="certificates-dup-note">{dupNote}</p>}
              {ticketFiles.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs" data-testid="certificates-status-list">
                  {ticketFiles.map((t) => (
                    <li key={t.id} className="flex items-center gap-2" data-testid={`certificate-status-${t.id}`}>
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${t.drive_status === 'done' ? 'bg-green-500' : t.drive_status === 'error' ? 'bg-red-500' : 'bg-blue-400 animate-pulse'}`} />
                      <span className="text-gray-800 truncate">
                        {t.drive_status === 'done' ? (t.ticket_name || 'Filed') : t.filename}
                        {t.drive_status === 'done' && t.expiry_date ? ` · exp ${t.expiry_date}` : ''}
                      </span>
                      <span className="text-gray-500 flex-shrink-0">
                        {t.drive_status === 'done' ? 'filed to Safety' : t.drive_status === 'error' ? (t.drive_error || 'could not file') : 'reading card…'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {uploading && <p className="text-sm text-blue-600 mt-3">Uploading…</p>}
          </div>

          {/* Void cheque upload — only if Direct Deposit was skipped */}
          {onboardingData?.direct_deposit_skipped && (
            <div className="mb-8" data-testid="splash-void-cheque-section">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <CloudArrowUpIcon className="h-6 w-6 mr-2" />
                3. Upload Your Void Cheque
                {onboardingData?.direct_deposit_voidcheque_uploaded && <CheckCircleIcon className="h-6 w-6 text-green-500 ml-2" />}
              </h2>
              {onboardingData?.direct_deposit_voidcheque_uploaded ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                  ✓ Your void cheque has been uploaded. Thank you!
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <p className="text-sm text-amber-900 mb-3">
                    You skipped the Direct Deposit form. Please upload a photo or PDF of your void cheque / direct deposit slip so payroll can set up your account. (You can also do this later from your dashboard.)
                  </p>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    disabled={uploading}
                    data-testid="splash-void-cheque-input"
                    onChange={(e) => handleVoidChequeUpload(e.target.files[0])}
                    className="text-sm"
                  />
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <div className="relative group">
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || uploading}
                className={`px-8 py-3 rounded-lg font-semibold ${
                  canSubmit && !uploading
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue to Dashboard
              </button>
              
              {/* Tooltip - shows when button is disabled */}
              {(!canSubmit || uploading) && (
                <div className="invisible group-hover:visible absolute bottom-full right-0 mb-2 w-80 bg-white text-slate-900 text-sm rounded-lg shadow-lg p-4 z-10">
                  <div className="font-semibold mb-2">Complete the following to submit:</div>
                  <ul className="space-y-1">
                    {getMissingItems().map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-red-600 mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {/* Arrow pointing down */}
                  <div className="absolute top-full right-8 -mt-2">
                    <div className="border-8 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSplash;
